/* eslint-disable no-console */
import { fg } from '@atlaskit/platform-feature-flags';

import {
	type OperationalEventAction,
	type OperationalEventAttributes,
	sendUnsampledPackageOperationalEvent,
} from '../analytics-client';

import { LOG_PREFIX } from './constants';
import { determineLogLevel } from './determine-log-level';
import { shouldSendErrorEvent } from './error-event-throttle';
import { LogLevel } from './log-level';

type LoggingMetadata = string | { [k: string]: string };

interface OperationalErrorLogConfig {
	action: OperationalEventAction;
	attributes?: OperationalEventAttributes;
	message: string;
	metadata?: LoggingMetadata;
}

const formatMetadata = (metadata?: LoggingMetadata): string => {
	if (!metadata) {
		return '';
	}
	// `LoggingMetadata` allows a bare string; emit it as-is rather than running it
	// through JSON.stringify, which would wrap it in extra quotes ("error-code" -> '"error-code"').
	return typeof metadata === 'string' ? metadata : JSON.stringify(metadata);
};

export class Logger {
	// All console output below ERROR (info/warn/debug) is gated behind
	// `platform_moonjelly_bsc_warn_on_set_blocked` so this package's new logging stays dark until
	// the flag is enabled. ERROR output is intentionally always on (subject only to the log level).
	//
	// The log-level comparison is evaluated FIRST in each guard so that `fg` is never called at
	// module-load time: at the default ERROR level these methods short-circuit before reaching
	// `fg`, which keeps a plain `import` of this package from evaluating feature gates (the same
	// import-time concern handled in determine-log-level.ts).
	static info(message: string, metadata?: LoggingMetadata): void {
		if (determineLogLevel() >= LogLevel.INFO && fg('platform_moonjelly_bsc_warn_on_set_blocked')) {
			console.info(`${LOG_PREFIX}: ${message}`, formatMetadata(metadata));
		}
	}

	static warn(message: string, metadata?: LoggingMetadata): void {
		if (determineLogLevel() >= LogLevel.WARN && fg('platform_moonjelly_bsc_warn_on_set_blocked')) {
			console.warn(`${LOG_PREFIX}: ${message}`, formatMetadata(metadata));
		}
	}

	static error(message: string, metadata?: LoggingMetadata): void {
		if (determineLogLevel() >= LogLevel.ERROR) {
			console.error(`${LOG_PREFIX}: ${message}`, formatMetadata(metadata));
		}
	}

	static debug(message: string, metadata?: LoggingMetadata): void {
		if (determineLogLevel() >= LogLevel.DEBUG && fg('platform_moonjelly_bsc_warn_on_set_blocked')) {
			console.debug(`${LOG_PREFIX}: DEBUG ${message}`, formatMetadata(metadata));
		}
	}

	/**
	 * Sends an unsampled operational event to backend telemetry and also emits the message via
	 * `Logger.error`. When `platform_bsc_throttle_error_events` is enabled the telemetry send is
	 * throttled per-action (see error-event-throttle); otherwise it is unconditional. The console
	 * output is always at ERROR level (subject to the resolved log level).
	 */
	static errorWithOperationalEvent({
		action,
		attributes = {},
		message,
		metadata,
	}: OperationalErrorLogConfig): void {
		try {
			// When the throttle gate is OFF the second operand is never evaluated, so no throttle
			// state is recorded and behaviour is unchanged. When ON, shouldSendErrorEvent records
			// the send and limits per-action volume.
			if (!fg('platform_bsc_throttle_error_events') || shouldSendErrorEvent(action)) {
				sendUnsampledPackageOperationalEvent({
					action,
					attributes: { error: message, ...attributes },
				});
			}
		} catch (e: any) {
			this.error(`Failed to send operational event on error: ${action}. ${e.message || ''}`);
		}

		this.error(message, metadata);
	}
}
